var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { EXPECTATION_FAILED, BAD_REQUEST, getReasonPhrase } from "http-status-codes";
import "isomorphic-fetch";
import cloneDeep from "lodash/cloneDeep";
import isPlainObject from "lodash/isPlainObject";
import merge from "lodash/merge";
import qs from "qs";
import { failureResToError, SurfaceableError } from "@novel/shared/utils/SurfaceableError";
import { AppEnv, AppEnvEnum } from "./env";
export function requestFailureStr(url, status, statusText, bodyText) {
    return `request failed to ${url} | ${status}${statusText ? `: ${statusText}` : ""}${bodyText ? `: ${bodyText}` : ""}`;
}
const defaultConfig = {
    credentials: "same-origin",
    referrerPolicy: "origin-when-cross-origin",
    mode: "cors",
    cache: "default",
};
function restReq(endpoint, _a) {
    var { params, query } = _a, requestConfig = __rest(_a, ["params", "query"]);
    // handling attaching of request body
    const requestConfigWithFinalizedBody = Object.assign(Object.assign({}, defaultConfig), requestConfig);
    if (requestConfig.reqBody) {
        if (isPlainObject(requestConfig.reqBody)) {
            requestConfigWithFinalizedBody.headers = Object.assign(Object.assign({}, requestConfigWithFinalizedBody.headers), { "Content-Type": "application/json" });
            requestConfigWithFinalizedBody.body = JSON.stringify(requestConfig.reqBody);
        }
        else if (typeof requestConfig.reqBody === "string") {
            requestConfigWithFinalizedBody.headers = Object.assign(Object.assign({}, requestConfigWithFinalizedBody.headers), { "Content-Type": "text/plain" });
            requestConfigWithFinalizedBody.body = requestConfig.reqBody;
        }
        else {
            // confused as to why I need to cheat here... no big deal though
            requestConfigWithFinalizedBody.body = requestConfig.reqBody;
        }
    }
    // handle applying params
    let endpointWithParams;
    if (!params) {
        endpointWithParams = endpoint;
    }
    else {
        endpointWithParams = Object.keys(params).reduce((accum, paramName) => {
            if (params) {
                return accum.replace(`:${paramName}`, encodeURIComponent(params[paramName]));
            }
            return accum;
        }, endpoint);
    }
    // handle applying query string
    let endpointWithQueryString;
    if (!query || Object.keys(query).length === 0) {
        endpointWithQueryString = endpointWithParams;
    }
    else {
        endpointWithQueryString = `${endpointWithParams}${qs.stringify(query, {
            addQueryPrefix: true,
        })}`;
    }
    return fetch(endpointWithQueryString, Object.assign({}, requestConfigWithFinalizedBody))
        .then((res) => {
        var _a;
        if (res.status >= BAD_REQUEST) {
            return res
                .text()
                .then((errorText) => {
                var _a, _b, _c;
                let parsedJson;
                try {
                    parsedJson = JSON.parse(errorText);
                }
                catch (e) {
                    // should never happen, but just in case
                    if (typeof errorText === "object") {
                        parsedJson = errorText;
                    }
                }
                // pass off to failure handling
                const status = (parsedJson === null || parsedJson === void 0 ? void 0 : parsedJson.status) || res.status;
                const message = (parsedJson === null || parsedJson === void 0 ? void 0 : parsedJson.message) ||
                    ((_a = parsedJson === null || parsedJson === void 0 ? void 0 : parsedJson.error) === null || _a === void 0 ? void 0 : _a.message) ||
                    (parsedJson === null || parsedJson === void 0 ? void 0 : parsedJson.errors) ||
                    (parsedJson === null || parsedJson === void 0 ? void 0 : parsedJson.messages) ||
                    (status && getReasonPhrase(status)) ||
                    errorText;
                const resolvedMessage = typeof message !== "string" ? JSON.stringify(message) : message;
                return Promise.reject({
                    status,
                    message: resolvedMessage,
                    verbose: !!((_b = parsedJson === null || parsedJson === void 0 ? void 0 : parsedJson.meta) === null || _b === void 0 ? void 0 : _b.verbose),
                    displayAsError: !!((_c = parsedJson === null || parsedJson === void 0 ? void 0 : parsedJson.meta) === null || _c === void 0 ? void 0 : _c.displayAsError),
                });
            })
                .catch((err) => {
                var _a, _b;
                // sloppy but everything else screwed up Typescript compiler
                if (err.message && err.status) {
                    return Promise.reject({
                        status: err.status,
                        message: err.message,
                        verbose: !!(err === null || err === void 0 ? void 0 : err.verbose) || !!((_a = err === null || err === void 0 ? void 0 : err.meta) === null || _a === void 0 ? void 0 : _a.verbose),
                        displayAsError: !!(err === null || err === void 0 ? void 0 : err.displayAsError) || !!((_b = err === null || err === void 0 ? void 0 : err.meta) === null || _b === void 0 ? void 0 : _b.displayAsError),
                    });
                }
                return Promise.reject(res);
            });
        }
        const contentType = (_a = res.headers) === null || _a === void 0 ? void 0 : _a.get("content-type");
        if (contentType) {
            if (contentType.includes("application/json")) {
                if (res.headers.get("content-length") === "0") {
                    return {
                        type: "success",
                        body: undefined,
                    };
                }
                return res.json().then((responseJson) => {
                    if (responseJson.success === false) {
                        const castedResponseJson = responseJson;
                        return {
                            type: "error",
                            status: castedResponseJson.status,
                            error: failureResToError(castedResponseJson),
                        };
                    }
                    return {
                        type: "success",
                        body: responseJson,
                    };
                });
            }
            else if (contentType.includes("application/text") ||
                contentType.includes("text/plain")) {
                return res.text().then((responseText) => ({
                    type: "success",
                    body: responseText,
                }));
            }
        }
        if (contentType) {
            const errorContentPromise = contentType.includes("text/html")
                ? res.text()
                : Promise.resolve("");
            return errorContentPromise.then((errorContent) => {
                const errorMessage = `got unhandled content-type header in response from ${endpointWithQueryString}, content-type: ${contentType}${errorContent ? `\n\n${errorContent}` : ""}`;
                // tslint:disable-next-line: no-console
                console.error(errorMessage);
                return Promise.reject({
                    status: EXPECTATION_FAILED,
                    message: errorMessage,
                });
            });
        }
        return {
            type: "success",
            body: undefined,
            headers: Array.from(res.headers.entries()).reduce((acc, [k, v]) => (Object.assign(Object.assign({}, acc), { [k]: v })), {}),
        };
    })
        .catch((errorRes) => {
        var _a;
        if (AppEnv !== AppEnvEnum.production) {
            // tslint:disable-next-line: no-console
            console.error(requestFailureStr(endpointWithQueryString, errorRes === null || errorRes === void 0 ? void 0 : errorRes.status, errorRes === null || errorRes === void 0 ? void 0 : errorRes.statusText, typeof errorRes === "object"
                ? JSON.stringify(errorRes)
                : errorRes === null || errorRes === void 0 ? void 0 : errorRes.toString()));
        }
        const message = ((errorRes === null || errorRes === void 0 ? void 0 : errorRes.message) || ((_a = errorRes === null || errorRes === void 0 ? void 0 : errorRes.toString) === null || _a === void 0 ? void 0 : _a.call(errorRes))) || "";
        const status = parseInt(errorRes === null || errorRes === void 0 ? void 0 : errorRes.status, 10) || 400;
        const error = (errorRes === null || errorRes === void 0 ? void 0 : errorRes.verbose)
            ? new SurfaceableError(message, !!(errorRes === null || errorRes === void 0 ? void 0 : errorRes.displayAsError))
            : new Error(message);
        error.status = status; // used by error middleware
        if (errorRes.stack) {
            error.stack = errorRes.stack;
        }
        return {
            type: "error",
            status,
            error,
        };
    });
}
function getReq(endpoint, requestConfig = {}) {
    return restReq(endpoint, Object.assign(Object.assign({}, requestConfig), { method: "GET" }));
}
function putReq(endpoint, requestConfig = {}) {
    return restReq(endpoint, Object.assign(Object.assign({}, requestConfig), { method: "PUT" }));
}
function postReq(endpoint, requestConfig = {}) {
    return restReq(endpoint, Object.assign(Object.assign({}, requestConfig), { method: "POST" }));
}
function patchReq(endpoint, requestConfig = {}) {
    return restReq(endpoint, Object.assign(Object.assign({}, requestConfig), { method: "PATCH" }));
}
function deleteReq(endpoint, requestConfig = {}) {
    return restReq(endpoint, Object.assign(Object.assign({}, requestConfig), { method: "DELETE" }));
}
export class RestReq {
    constructor(prefix = "", requestConfig = {}, middlewares = []) {
        this.getFinalRequestConfig = (endpoint, requestConfig = {}) => this.middlewares.reduce((accum, currentMiddleware) => currentMiddleware(endpoint, accum), merge(cloneDeep(this.requestConfig), requestConfig));
        this.prefix = prefix;
        this.requestConfig = requestConfig;
        this.middlewares = middlewares;
    }
    getReq(endpoint, requestConfig = {}) {
        return getReq(`${this.prefix}${endpoint}`, this.getFinalRequestConfig(endpoint, requestConfig));
    }
    putReq(endpoint, requestConfig = {}) {
        return putReq(`${this.prefix}${endpoint}`, this.getFinalRequestConfig(endpoint, requestConfig));
    }
    postReq(endpoint, requestConfig = {}) {
        return postReq(`${this.prefix}${endpoint}`, this.getFinalRequestConfig(endpoint, requestConfig));
    }
    patchReq(endpoint, requestConfig = {}) {
        return patchReq(`${this.prefix}${endpoint}`, this.getFinalRequestConfig(endpoint, requestConfig));
    }
    deleteReq(endpoint, requestConfig = {}) {
        return deleteReq(`${this.prefix}${endpoint}`, this.getFinalRequestConfig(endpoint, requestConfig));
    }
}
export const untypedReq = new RestReq();
